<template>
  <d-container
    fluid
    :class="{ 'main-content-container p-0': true }">
    <d-card
      class="week mb-0">
      <div class="week-heading p-3">
        <d-datepicker
          v-model="dayFromCalendar"
          :monday-first="true"
          format="D, d MMMM yyyy"
          :placeholder="$t('ui.chooseDate')"
          :language="translationsForDatePicker" />

        <div class="week-nav-top">
          <a
            href="#prev-month"
            :class="{ 'week-nav-top-prev': true, 'is-inactive': !previousWeekIsAvailable }"
            @click.prevent="goToPreviousWeek">
          </a>

          <div class="week-period">
            {{ currentPeriod.date_from }} - {{ currentPeriod.date_to }}
          </div>

          <a
            href="#next-month"
            class="week-nav-top-next"
            @click.prevent="goToNextWeek">
          </a>
        </div>

        <d-button
          outline
          theme="primary"
          class="ml-auto"
          @click.prevent="showToday"
          :title="$t('calendar.today')">
          {{ $t('calendar.showCurrentWeek') }}
        </d-button>
      </div>

      <div
        :class="{ 'week-days': true, 'pl-3': true, 'pr-3': true }">
        <week-day
          v-for="(dayData, index) of visibleDays"
          :key="'day-item-' + index"
          :ref="'week-day-' + index"
          :dayData="dayData"
          :dayIndex="index"
          :ordersData="itemsByDate[dayData.date]" />
      </div>
    </d-card>
  </d-container>
</template>

<script>
import CalendarUtils from './../../utils/CalendarUtils';
import datepickerTranslations from './../../data/datepicker-translations.js';
import DaysNames from './../../data/days-names.js';
import FormUtils from './../../utils/FormUtils.js';
import WeekDay from './WeekDay';
import getPrefferedLanguage from './../../utils/i18nUtils.js';

export default {
  name: 'week',
  props: [
    'loadedItems'
  ],
  components: {
    'week-day': WeekDay
  },
  data () {
    return {
      dayFromCalendar: null,
      itemsByDate: {},
      currentYear: null,
      currentDay: null,
      currentMonth: null
    };
  },
  watch: {
    'loadedItems': function () {
      this.generateItems();
    },
    'dayFromCalendar': function (newValue) {
      if (!newValue) {
        return;
      }

      let newYear = this.dayFromCalendar.getFullYear();
      let newMonth = this.dayFromCalendar.getMonth() + 1;
      let newDay = this.dayFromCalendar.getDate();
      this.updatePeriod(newYear, newMonth, newDay);
    }
  },
  computed: {
    daysNames () {
      return DaysNames[getPrefferedLanguage()];
    },
    period () {
      return CalendarUtils.getWeekPeriod(this.currentYear, this.currentMonth, this.currentDay);
    },
    visibleDays () {
      return CalendarUtils.getWeekDaysList(this.currentYear, this.currentMonth, this.currentDay);
    },
    previousWeekIsAvailable () {
      if (this.currentDay === 1 && this.currentYear === 2020) {
        return false;
      }

      return true;
    },
    currentPeriod () {
      return {
        date_from: CalendarUtils.getWeekStartDay(this.currentYear, this.currentMonth, this.currentDay),
        date_to: CalendarUtils.getWeekEndDay(this.currentYear, this.currentMonth, this.currentDay)
      }
    },
    translationsForDatePicker () {
      return datepickerTranslations[getPrefferedLanguage()];
    }
  },
  mounted () {
    let today = new Date();
    this.currentYear = today.getFullYear();
    this.currentMonth = today.getMonth() + 1;
    this.currentDay = today.getDate();
    this.$bus.$on('services-schedule-week-days-ordering-save', this.saveItemsOrder);
  },
  methods: {
    goToPreviousWeek () {
      let date = new Date([this.currentYear, this.currentMonth, this.currentDay].map(n => n < 10 ? '0' + n : n).join('-'));
      date.setDate(date.getDate() - 7);
      this.updatePeriod(date.getFullYear(), date.getMonth() + 1, date.getDate());
    },
    goToNextWeek () {
      let date = new Date([this.currentYear, this.currentMonth, this.currentDay].map(n => n < 10 ? '0' + n : n).join('-'));
      date.setDate(date.getDate() + 7);
      this.updatePeriod(date.getFullYear(), date.getMonth() + 1, date.getDate());
    },
    updatePeriod (newYear, newMonth, newDay) {
      this.currentYear = parseInt(newYear, 10);
      this.currentMonth = parseInt(newMonth, 10);
      this.currentDay = parseInt(newDay, 10);
      this.$bus.$emit('services-schedule-calendar-refresh');
    },
    showToday () {
      let today = new Date();
      this.updatePeriod(today.getFullYear(), today.getMonth() + 1, today.getDate());
    },
    generateItems () {
      if (!this.loadedItems || !this.loadedItems.length) {
        this.itemsByDate = {};
      }

      let items = {};

      for (let i = 0; i < this.loadedItems.length; i++) {
        let date = this.loadedItems[i].date;

        items[date] = {
          items: this.loadedItems[i].items,
          orders_qty: parseInt(this.loadedItems[i].orders_count, 10),
          qty: parseInt(this.loadedItems[i].quantities_count, 10)
        };
      }

      for (let i = 0; i < this.visibleDays.length; i++) {
        let date = this.visibleDays[i].date;

        if (!items[date]) {
          items[date] = {
            items: [],
            orders_qty: 0,
            qty: 0
          };
        }
      }

      this.itemsByDate = JSON.parse(JSON.stringify(items));
    },
    saveItemsOrder () {
      this.$bus.$emit('services-schedule-week-order-change', true);

      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/services-schedule/ordering',
        formData: this.generateOrderingData(),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('calendar.orderChangeUnsuccessful'),
        successAction: () => {
          this.$bus.$emit('services-schedule-calendar-refresh');

          setTimeout(() => {
            this.$bus.$emit('services-schedule-week-order-change', false);
          }, 300);
        }
      });
    },
    generateOrderingData () {
      let output = {
        dates: []
      };

      for (let i = 0; i < this.visibleDays.length; i++) {
        let date = this.visibleDays[i].date;
        this.itemsByDate[date].items = this.$refs['week-day-' + i][0].dayItems;

        if (this.itemsByDate[date] && this.itemsByDate[date].items && this.itemsByDate[date].items.length) {
          output.dates.push({
            date,
            items: this.itemsByDate[date].items.map(item => item.id)
          });
        }
      }

      return output;
    }
  },
  beforeDestroy () {
    this.$bus.$off('services-schedule-week-days-ordering-save', this.saveItemsOrder);
  }
}
</script>

<style lang="scss" scoped>
.change-in-progress {
  opacity: .5;
  pointer-events: none;
}

.week {
  background: #FFF;
  border: 1px solid #CACEDB;
  margin: 0 auto 50px auto;
  max-width: 1400px;
  padding: 0;
  position: relative;
  width: 100%;

  &-heading {
    align-items: center;
    display: flex;
    padding: 2rem;
    position: relative;
    width: 100%;

    h1 {
      font-size: 2.4rem;
      font-weight: 400;
      line-height: 1;
      margin: 0;

      span {
        color: #007bff;
      }
    }

    & > .button {
      margin-left: auto;
      margin-right: 0;
    }

    .week-period {
      font-size: 20px;
      text-transform: capitalize;
    }
  }

  &-days {
    display: flex;
  }

  &-nav {
    align-items: center;
    display: flex;
    height: 80px;
    justify-content: center;

    .button {
      justify-content: center;
      width: 150px;
    }

    &-top {
      display: flex;
      left: 50%;
      padding-top: 1px;
      position: absolute;
      top: 50%;
      transform: translateX(-50%) translateY(-50%);

      &-prev,
      &-next {
        width: 20px;

        &:before,
        &:after {
          background: #007bff;
          content: "";
          display: block;
          height: 2px;
          position: absolute;
          top: 50%;
          transform-origin: 0 50%;
          width: 14px;
        }
      }

      &-prev {
        margin-right: 20px;

        &:before,
        &:after {
          left: 0;
        }

        &:before {
          transform: rotate(-45deg);
        }

        &:after {
          transform: rotate(45deg);
        }

        &.is-inactive {
          opacity: .25;
          pointer-events: none;
        }
      }

      &-next {
        margin-left: 20px;

        &:before,
        &:after {
          transform-origin: 100% 50%;
          right: 0;
        }

        &:before {
          transform: rotate(45deg);
        }

        &:after {
          transform: rotate(-45deg);
        }
      }
    }
  }

  /deep/ .vdp-datepicker input.form-control {
    text-align: center;
  }
}
</style>
